// SalesChart.js
// Chart showing total sales value over time

// Imports
import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@blueprintjs/core';
import TimeSeriesChart from './TimeSeriesChart';
import { ValueType } from './chartEnums';

// Returns the total value of a closed (archived) order.
// Sums all product prices and adjustments across all clients.
export function getArchivedOrderTotal(order) {
  let total = 0;
  for (const clientId in order.byClient) {
    const clientData = order.byClient[clientId];
    for (const key in clientData) {
      if (key === 'adjustment') {
        // Include adjustment (can be negative for discounts)
        if (clientData.adjustment && clientData.adjustment.value) {
          total += clientData.adjustment.value;
        }
      } else {
        const product = clientData[key];
        if (product && product.price !== undefined) {
          total += product.price;
        }
      }
    }
  }
  return total;
}

// Returns the total value of the currently open order, computed from products data.
export function getOpenOrderTotal(currentOrder, products) {
  if (!currentOrder || !products || !products.byId) return 0;
  let total = 0;
  for (const clientId in currentOrder.byClient) {
    const clientData = currentOrder.byClient[clientId];
    for (const key in clientData) {
      if (key === 'adjustment') {
        if (clientData.adjustment && clientData.adjustment.value) {
          total += clientData.adjustment.value;
        }
      } else {
        const productOrder = clientData[key];
        const prod = products.byId[key];
        if (prod && productOrder) {
          const unitIdx = productOrder.unitId >= prod.price.length
            ? prod.price.length - 1
            : productOrder.unitId;
          const price = productOrder.customPrice
            ? productOrder.customPrice
            : prod.price[unitIdx].price * productOrder.quantity;
          total += price;
        }
      }
    }
  }
  return total;
}

// Converts orders state to TimeSeriesChart data points.
// `includeOpen` controls whether the currently open order is included (using today as reference date).
export function convertOrdersToDataPoints(orders, products, includeOpen) {
  const dataPoints = orders.archive
    .filter(order => order.dateClosed)
    .map(order => ({
      x: new Date(order.dateClosed),
      y: getArchivedOrderTotal(order),
    }));

  if (includeOpen && orders.current) {
    const hasItems = Object.keys(orders.current.byClient).length > 0;
    if (hasItems) {
      dataPoints.push({
        x: new Date(),
        y: getOpenOrderTotal(orders.current, products),
      });
    }
  }

  return dataPoints;
}

// Component Class
// Shows total sales value as a time series chart
class SalesChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { includeOpen: true };
  }

  handleToggleOpen = (e) => {
    this.setState({ includeOpen: e.target.checked });
  }

  // Renders Component
  render() {
    const { orders, products } = this.props;
    const { includeOpen } = this.state;
    const dataPoints = convertOrdersToDataPoints(orders, products, includeOpen);

    return (
      <div>
        <h3 style={{ textAlign: 'center' }} className="pt-heading">Vendas ao Longo do Tempo</h3>
        <div style={{ marginBottom: '10px' }}>
          <Checkbox
            id="include-open-orders"
            checked={includeOpen}
            onChange={this.handleToggleOpen}
            label="Incluir pedidos em aberto"
          />
        </div>
        <TimeSeriesChart
          datasets={[{
            label: 'Valor',
            dataPoints: dataPoints,
            color: 'rgba(45, 114, 210, 1)',
          }]}
          valueType={ValueType.CURRENCY}
        />
      </div>
    );
  }
}

// Required Properties
SalesChart.propTypes = {
  orders: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
};

// Exports
export default SalesChart;



// WEBPACK FOOTER //
// ./src/components/ChartView/SalesChart.js